import { Injectable } from "@angular/core";
import { last, padStart, sum, toPairs } from "lodash";
import { BehaviorSubject } from "rxjs";
import { appState } from "src/app/core/state";
import { consoleGrouped } from "src/app/support/console";
import {
  scannderDisable,
  scannerDecode,
  scannerEnable
} from "src/app/support/scanner";

export type ScannerStatus = "enabled" | "disabled";

export enum ScannerStatusType {
  DATA_MATRIX = "DATA_MATRIX",
  BARCODE = "BARCODE"
}

export type ScanResult = {
  type: ScannerStatusType;
  raw: string;
  upc?: string;
  gtin13?: string;
  gtin14?: string;
  minsan?: string;
  ean13?: string;
  ean14?: string;
  originalGtinIs14: boolean;
  batchNumber?: string;
  price?: string;
  useByOrExpiry?: string;
};

type ScannerServiceCallback = (result: ScanResult) => void;

@Injectable()
export class ScannerService {
  status = new BehaviorSubject<ScannerStatus>("disabled");

  private readonly consumers: Array<ScannerServiceCallback> = [];

  enable({ onScan }: { onScan: ScannerServiceCallback }): void {
    this.consumers.push(onScan);

    if (this.status.value === "enabled") {
      return;
    }

    this.activateScanner();
  }

  private activateScanner(): void {
    setTimeout(() => {
      scannerEnable({
        onScan: async (rawScanCode: string) => {
          let scanCode = rawScanCode;
          console.log("scanCode", scanCode);

          if (this.consumers.length === 0) {
            return;
          }

          try {
            const decodeResult = await scannerDecode(scanCode);

            const scanResult = <ScanResult>{
              type: ScannerStatusType.DATA_MATRIX,
              raw: scanCode,
              upc: decodeResult.upc,
              gtin13: decodeResult.gtin13,
              gtin14: decodeResult.gtin14,
              originalGtinIs14: decodeResult.originalGtinIs14,
              batchNumber: decodeResult.batchLot,
              useByOrExpiry: decodeResult.useByOrExpiry,
              price: decodeResult.price
            };

            consoleGrouped("Scan result", () => scanResult);

            last(this.consumers)!(scanResult);

            return;
          } catch (ex) {
            console.log(ex);
          }

          let price;
          let minsan;
          let ean13;
          let ean14;

          if (scanCode.length === 13) {
            ean13 = scanCode;
            ean14 = padStart(scanCode, 14, "0");
          }

          switch (true) {
            case isMinSanWithCharaterA(scanCode) &&
              validateCode32(scanCode.substring(1)): {
              console.log(
                `[ScannerService] found decoded minsan starting with "A"`
              );
              minsan = scanCode.substring(1);
              break;
            }
            case validateCode32(scanCode): {
              console.log(`[ScannerService] found decoded minsan`);
              minsan = scanCode;
              break;
            }
            default: {
              try {
                minsan = decodeBarcode(scanCode.substring(scanCode.length - 6));
                console.log(`[ScannerService] found encoded minsan`);
              } catch (ex) {
                console.error(ex);
              }
              break;
            }
          }

          const scanResult = <ScanResult>{
            type: ScannerStatusType.BARCODE,
            raw: scanCode,
            upc: scanCode?.length === 12 ? scanCode : undefined,
            price,
            minsan,
            ean13,
            ean14
          };

          consoleGrouped("Scan result", () => scanResult);

          last(this.consumers)!(scanResult);
        }
      });
      this.status.next("enabled");
    }, 0);
  }

  disable(): void {
    this.consumers.pop();

    if (this.consumers.length === 0) {
      scannderDisable();
      this.status.next("disabled");
    }
  }
}

const MAP: Record<string, number> = {
  "0": 0,
  "1": 1,
  "2": 2,
  "3": 3,
  "4": 4,
  "5": 5,
  "6": 6,
  "7": 7,
  "8": 8,
  "9": 9,
  B: 10,
  C: 11,
  D: 12,
  F: 13,
  G: 14,
  H: 15,
  J: 16,
  K: 17,
  L: 18,
  M: 19,
  N: 20,
  P: 21,
  Q: 22,
  R: 23,
  S: 24,
  T: 25,
  U: 26,
  V: 27,
  W: 28,
  X: 29,
  Y: 30,
  Z: 31
};

function decodeBarcode(barcode: string): string {
  return padStart(
    sum([
      MAP[barcode[0]] * Math.pow(32, 5),
      MAP[barcode[1]] * Math.pow(32, 4),
      MAP[barcode[2]] * Math.pow(32, 3),
      MAP[barcode[3]] * Math.pow(32, 2),
      MAP[barcode[4]] * 32,
      MAP[barcode[5]]
    ]).toString(),
    9,
    "0"
  );
}

function encodeBarcode(code: string): string {
  if (code.toLowerCase().startsWith("A")) {
    code = code.substring(1);
  }

  let ret = "";

  let cursor = parseInt(code);

  for (let i = 5; i >= 0; i--) {
    const div = Math.floor(cursor / Math.pow(32, i));
    const rest = cursor % Math.pow(32, i);

    ret += toPairs(MAP).find(([key, value]) => value === div)![0];

    cursor = rest;
  }

  return ret;
}

function validateCode32(code: string): boolean {
  if (code.length !== 9) {
    return false;
  }

  const digits = code
    .substring(0, 8)
    .split("")
    .map((char) => parseInt(char));
  const checkDigit = parseInt(code.substring(8));

  const P = sum(
    [1, 3, 5, 7].map((index) => {
      const x = digits[index] * 2;

      const quotient = Math.floor(x / 10);
      const rest = x % 10;

      return quotient + rest;
    })
  );

  const D = sum(
    [0, 2, 4, 6].map((index: number) => {
      return digits[index];
    })
  );

  const T = P + D;

  return T % 10 === checkDigit;
}

function isMinSanWithCharaterA(code: string): boolean {
  return code.toLowerCase().startsWith("a");
}
